/**
 * One predicate in full: the book's words for it, what it costs, and what it refuses to encode.
 *
 * The module docstring is read out of the running code rather than restated here, so this page
 * cannot drift from the predicate it is describing. The degradation figures beneath it are the
 * only evidence that the rule is doing anything: break the book in the way the rule is about,
 * and its fault rate has to rise.
 */

import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import Board from "../components/Board";
import { ACTION } from "../components/Chrome";
import { Failed, Kicker, Loading, Note } from "../components/Page";
import { count, rate } from "../lib/format";

export default function RulePage(): JSX.Element {
  const { predicate = "" } = useParams();
  const { data, error, loading } = useAsync(`rule:${predicate}`, () => api.rule(predicate));

  const risen = data === null ? [] : data.degradations.filter((one) => one.rise !== null);

  return (
    <Board
      kicker={data === null ? "One rule" : `Rule ${data.position} of ${data.of}`}
      title={data?.predicate ?? predicate}
      back={{ to: "/rules", label: "The eight rules" }}
      actions={
        <Link to="/evidence/figures" className={ACTION}>
          On the book&apos;s figures
        </Link>
      }
    >
      {loading ? (
        <Loading what={`the ${predicate} rule`} />
      ) : error !== null ? (
        <Failed error={error} />
      ) : data === null ? (
        <Failed error="that rule came back empty" />
      ) : (
        <div className="space-y-7">
          <p className="font-display text-xl leading-snug">{data.statement}</p>
          <p className="text-sm text-ink-faded">
            {data.source} · {data.scope}
          </p>

          <section className="space-y-2">
            <Kicker>What it costs</Kicker>
            <div className="rounded-xl border border-rule bg-leaf p-5">
              {data.total === null ? (
                <Note>Not measured yet — the corpus snapshot has not been built.</Note>
              ) : (
                <>
                  <div className="flex items-baseline gap-1">
                    <span className="font-mono text-[38px] leading-none">
                      {rate(data.total.score).replace("%", "")}
                    </span>
                    <span className="font-mono text-base text-ink-quiet">%</span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-ink-faded">
                    of the book called clean · {count(data.total.clean)} of{" "}
                    {count(data.total.judged)} judged · heard in {count(data.total.spokeIn)} of 314
                    scores · {count(data.total.faults)} faults
                  </p>
                </>
              )}
            </div>
          </section>

          {risen.length > 0 ? (
            <section className="space-y-2">
              <Kicker>Break the book on purpose</Kicker>
              <div className="overflow-hidden rounded-xl border border-rule bg-leaf">
                {risen.map((one) => (
                  <div
                    key={one.name}
                    className="flex items-baseline gap-3 border-b border-rule-faint px-4 py-3 last:border-b-0"
                  >
                    <span className="w-24 shrink-0 font-mono text-xs">{one.name}</span>
                    <span className="flex-1 text-xs leading-relaxed text-ink-faded">
                      {one.description}
                    </span>
                    <span
                      className={`shrink-0 font-mono text-sm ${
                        (one.rise ?? 0) > 0 ? "text-fault" : "text-ink-quiet"
                      }`}
                    >
                      {one.rise === null
                        ? "—"
                        : `${one.rise > 0 ? "+" : ""}${one.rise.toFixed(1)} pp`}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs leading-relaxed text-ink-faded">
                Each mutation is aimed at this rule, so its fault rate is what has to rise. A rise
                of nothing would mean the predicate is not measuring what it claims to.
              </p>
            </section>
          ) : null}

          <section className="space-y-2">
            <Kicker>As the code documents itself</Kicker>
            <pre className="overflow-x-auto whitespace-pre-wrap rounded-xl border border-rule bg-leaf p-4 font-mono text-[12px] leading-relaxed text-ink-faded">
              {data.detail ?? "This predicate carries no module documentation."}
            </pre>
            <p className="font-mono text-[11px] text-ink-quiet">{data.module}</p>
          </section>
        </div>
      )}
    </Board>
  );
}
